import { useEffect, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const Swatches = ({ swatches, active, onSelect }: { swatches: { name: string; hex: string }[]; active: string; onSelect: (hex: string) => void }) => (
  <div className="grid grid-cols-6 gap-2 pt-3">
    {swatches.map((s) => (
      <button
        key={s.hex}
        type="button"
        title={s.name}
        onClick={() => onSelect(s.hex)}
        className={`h-10 w-full rounded-md border transition-all ${active.toLowerCase() === s.hex.toLowerCase() ? "ring-2 ring-primary border-primary" : "border-border hover:scale-105"}`}
        style={{ background: s.hex }}
      />
    ))}
  </div>
);

export const BrandSlots = ({
  slots,
  activeHex,
  onApply,
  onSave,
  onDelete,
}: {
  slots: { id?: string; slot_index: number; label: string | null; hex: string }[];
  activeHex: string;
  onApply: (hex: string) => void;
  onSave: (slot_index: number, hex: string, label: string) => void;
  onDelete: (slot_index: number) => void;
}) => {
  const total = Math.max(12, slots.length + (slots.length % 12 === 0 ? 0 : 0));
  const items = Array.from({ length: total }).map((_, i) => slots.find((s) => s.slot_index === i) ?? null);

  return (
    <div className="grid grid-cols-6 gap-2 pt-3">
      {items.map((slot, i) => (
        <BrandSlotCell
          key={i}
          slot={slot}
          slotIndex={i}
          isActive={!!slot && activeHex.toLowerCase() === slot.hex.toLowerCase()}
          onApply={onApply}
          onSave={onSave}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};

const BrandSlotCell = ({
  slot,
  slotIndex,
  isActive,
  onApply,
  onSave,
  onDelete,
}: {
  slot: { hex: string; label: string | null } | null;
  slotIndex: number;
  isActive: boolean;
  onApply: (hex: string) => void;
  onSave: (slot_index: number, hex: string, label: string) => void;
  onDelete: (slot_index: number) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState(slot?.hex ?? "#3B82F6");
  const [label, setLabel] = useState(slot?.label ?? "");

  useEffect(() => {
    setHex(slot?.hex ?? "#3B82F6");
    setLabel(slot?.label ?? "");
  }, [slot]);

  if (!slot) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" title="Add brand color" className="flex h-10 w-full items-center justify-center rounded-md border border-dashed border-border text-muted-foreground hover:bg-muted">
            <Plus className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 space-y-3">
          <HexColorPicker color={hex} onChange={setHex} style={{ width: "100%" }} />
          <Input value={hex} onChange={(e) => setHex(e.target.value)} />
          <Input placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} />
          <Button
            size="sm"
            className="w-full"
            onClick={() => {
              onSave(slotIndex, hex, label);
              setOpen(false);
            }}
          >
            Save color
          </Button>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={slot.label ?? slot.hex}
          onClick={(e) => {
            if (e.detail === 1) {
              onApply(slot.hex);
              e.preventDefault();
            }
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            setOpen(true);
          }}
          onDoubleClick={() => setOpen(true)}
          className={`h-10 w-full rounded-md border ${isActive ? "ring-2 ring-primary border-primary" : "border-border hover:scale-105"}`}
          style={{ background: slot.hex }}
        />
      </PopoverTrigger>
      <PopoverContent className="w-64 space-y-3">
        <HexColorPicker color={hex} onChange={setHex} style={{ width: "100%" }} />
        <Input value={hex} onChange={(e) => setHex(e.target.value)} />
        <Input placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} />
        <div className="flex gap-2">
          <Button
            size="sm"
            className="flex-1"
            onClick={() => {
              onSave(slotIndex, hex, label);
              setOpen(false);
            }}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              onDelete(slotIndex);
              setOpen(false);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Click swatch to apply · double-click or right-click to edit.</p>
      </PopoverContent>
    </Popover>
  );
};
